/**
 * The first thing the app shows when it is locked.
 *
 * Signing in and creating an account are both offered up front rather than
 * inferred from whether the device happens to hold an account already. A
 * driver who starts a second, separate logbook should not have to discover
 * that possibility underneath a password field.
 *
 * On a device with no account the sign-in choice is shown but disabled: it
 * stays visible so the pair of options is always the same, and disabled
 * because there is nothing yet to sign in to.
 */

import { useEffect, useState } from 'react';
import type { AccountRecord } from '@/crypto/vault';
import { listAccounts } from '@/session/SessionContext';
import { useT } from '@/i18n';
import { AuthShell } from './SetupScreen';

interface WelcomeScreenProps {
  onSignIn: () => void;
  onRegister: () => void;
}

export function WelcomeScreen({ onSignIn, onRegister }: WelcomeScreenProps) {
  const t = useT();
  const [accounts, setAccounts] = useState<AccountRecord[] | null>(null);

  useEffect(() => {
    void (async () => setAccounts(await listAccounts()))();
  }, []);

  // Until the registry has been read, neither branch is known to be valid, so
  // both wait rather than flickering from enabled to disabled.
  const loading = accounts === null;
  const sole = accounts?.length === 1 ? accounts[0] : undefined;
  const canSignIn = !loading && accounts.length > 0;

  return (
    <AuthShell
      title={t.welcome.title}
      subtitle={sole ? t.login.greeting(sole.displayName) : t.welcome.subtitle}
    >
      <div className="space-y-3">
        <button
          type="button"
          className="btn-primary w-full"
          onClick={onSignIn}
          disabled={!canSignIn}
        >
          {t.login.signIn}
        </button>

        <button
          type="button"
          className="btn-secondary w-full"
          onClick={onRegister}
          disabled={loading}
        >
          {t.login.register}
        </button>
      </div>

      <p className="text-muted mt-5 text-center text-xs leading-relaxed">
        {canSignIn ? t.welcome.separateNote : t.welcome.noAccountYet}
      </p>
    </AuthShell>
  );
}
